/**
 * Custom hook for CSV import and related operations.
 * Handles CSV uploads (append), history imports, genus changes, and related syncs.
 */
import { useState, useCallback } from 'react';

interface UseCsvOperationsProps {
  subreddit: string | null;
  user: { username: string } | null;
  setMessage: (msg: { type: 'success' | 'error' | 'warning'; text: string; details?: string } | null) => void;
  loadMasterSheet: (forceReload?: boolean, quickLoad?: boolean) => Promise<void>;
  loadTableData: (tableName: string, filters?: { class?: string; family?: string; genus?: string; flair?: string }, forceReload?: boolean) => Promise<void>;
  setTableDataCache: React.Dispatch<React.SetStateAction<{ [key: string]: any[] }>>;
  setMasterSheetCacheKey: (key: string | null) => void;
  activeTab: string | null;
  selectedClass: string | null;
  selectedFamily: string | null;
  selectedGenus: string | null;
  selectedFlair: string | null;
}

export function useCsvOperations({
  subreddit,
  user,
  setMessage,
  loadMasterSheet,
  loadTableData,
  setTableDataCache,
  setMasterSheetCacheKey,
  activeTab,
  selectedClass,
  selectedFamily,
  selectedGenus,
  selectedFlair,
}: UseCsvOperationsProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedHistoryFiles, setSelectedHistoryFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isImportingHistory, setIsImportingHistory] = useState(false);
  const [selectedGenusCsvFile, setSelectedGenusCsvFile] = useState<File | null>(null);
  const [isImportingGenusChanges, setIsImportingGenusChanges] = useState(false);
  const [selectedFadingCsvFile, setSelectedFadingCsvFile] = useState<File | null>(null);
  const [isImportingFading, setIsImportingFading] = useState(false);
  const [isSyncingMasterSheet, setIsSyncingMasterSheet] = useState(false);
  const [fadedAssetsClassInput, setFadedAssetsClassInput] = useState('');
  const [isGeneratingFadedAssets, setIsGeneratingFadedAssets] = useState(false);
  const [fadeGenusInput, setFadeGenusInput] = useState('');
  const [fadeGenusIncludeBandwagons, setFadeGenusIncludeBandwagons] = useState(true);
  const [isFadingGenus, setIsFadingGenus] = useState(false);
  const [fadeGenusPreview, setFadeGenusPreview] = useState<null | {
    genusName: string;
    action: string;
    count: number;
    images: { image_name: string; flair_code?: string | null; currently_faded?: boolean; bandwagon?: boolean }[];
    bandwagons?: string[];
  }>(null);

  const handleImportHistory = useCallback(async () => {
    if (selectedHistoryFiles.length === 0 || !subreddit || !user) return;

    const BATCH_SIZE = 10; // Avoid proxy/body size limits by batching
    const files = [...selectedHistoryFiles];
    const totalBatches = Math.ceil(files.length / BATCH_SIZE);

    setIsImportingHistory(true);
    setMessage(null);

    const aggregated = { processed: 0, total_rows: 0, dates_imported: [] as string[], errors: [] as string[] };
    let failedBatches = 0;

    try {
      const { API_BASE_URL } = await import('../../../config');

      for (let batchStart = 0; batchStart < files.length; batchStart += BATCH_SIZE) {
        const batch = files.slice(batchStart, batchStart + BATCH_SIZE);
        const batchNum = Math.floor(batchStart / BATCH_SIZE) + 1;
        setMessage({
          type: 'success',
          text: `Importing batch ${batchNum}/${totalBatches} (${batch.length} files)...`,
        });

        const formData = new FormData();
        batch.forEach((file, i) => {
          formData.append('csvs', file);
          if (file.lastModified) {
            formData.append(`file_${i}_lastModified`, file.lastModified.toString());
            formData.append(`file_${i}_name`, file.name);
          }
        });

        const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/import-history`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${user.username}` },
          body: formData,
        });

        const data = await response.json();

        if (response.ok && data.results) {
          aggregated.processed += data.results.processed ?? 0;
          aggregated.total_rows += data.results.total_rows ?? 0;
          if (data.results.dates_imported?.length) {
            aggregated.dates_imported.push(...data.results.dates_imported);
          }
          if (data.results.errors?.length) {
            aggregated.errors.push(...data.results.errors);
          }
        } else {
          failedBatches++;
          aggregated.errors.push(`Batch ${batchNum}: ${data.error || data.details || response.statusText}`);
        }
      }

      if (failedBatches === 0) {
        setMessage({
          type: 'success',
          text: `Successfully imported history from ${aggregated.processed} CSV files!`,
          details: aggregated.dates_imported.length
            ? `Dates imported: ${aggregated.dates_imported.join(', ')}`
            : undefined,
        });
        setSelectedHistoryFiles([]);
        const fileInput = document.getElementById('history-csv-upload-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else if (failedBatches < totalBatches) {
        setMessage({
          type: 'warning',
          text: `Imported ${aggregated.processed} files, but ${failedBatches} batch(es) failed.`,
          details: aggregated.errors.slice(0, 5).join('; '),
        });
      } else {
        setMessage({
          type: 'error',
          text: `All ${totalBatches} batches failed.`,
          details: aggregated.errors.slice(0, 5).join('; '),
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to import history: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsImportingHistory(false);
    }
  }, [selectedHistoryFiles, subreddit, user, setMessage]);

  const handleImportGenusChanges = useCallback(async () => {
    if (!selectedGenusCsvFile || !subreddit || !user) return;
    
    setIsImportingGenusChanges(true);
    setMessage(null);
    
    try {
      const { API_BASE_URL } = await import('../../../config');
      const formData = new FormData();
      formData.append('csv', selectedGenusCsvFile);
      
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/import-genus-changes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
        body: formData,
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const processed = data.processed || 0;
        const errors = data.errors || [];
        const skipped = data.skipped || [];
        
        // Build comprehensive message
        let messageText = '';
        let messageType: 'success' | 'error' | 'warning' = 'success';
        const detailsParts: string[] = [];
        
        if (processed > 0) {
          messageText = `Successfully processed ${processed} genus change${processed !== 1 ? 's' : ''}`;
        } else {
          messageText = 'No genus changes were processed';
          messageType = 'warning';
        }
        
        if (errors.length > 0) {
          messageType = errors.length === processed ? 'error' : 'warning';
          detailsParts.push(`Errors (${errors.length}):`);
          errors.forEach((error: string, index: number) => {
            detailsParts.push(`${index + 1}. ${error}`);
          });
        }
        
        if (skipped.length > 0) {
          if (detailsParts.length > 0) detailsParts.push('');
          detailsParts.push(`Skipped (${skipped.length}):`);
          skipped.forEach((item: string, index: number) => {
            detailsParts.push(`${index + 1}. ${item}`);
          });
        }
        
        setMessage({
          type: messageType,
          text: messageText,
          details: detailsParts.length > 0 ? detailsParts.join('\n') : undefined
        });
        setSelectedGenusCsvFile(null);
        const fileInput = document.getElementById('genus-csv-upload-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // Clear cache and reload
        setTableDataCache({});
        setMasterSheetCacheKey('');
        if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
          const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
          if (selectedClass) filters.class = selectedClass;
          if (selectedFamily) filters.family = selectedFamily;
          if (selectedGenus) filters.genus = selectedGenus;
          if (selectedFlair) filters.flair = selectedFlair;
          loadTableData(activeTab, filters, true);
        }
        loadMasterSheet(true);
      } else {
        setMessage({
          type: 'error',
          text: data.error || data.details || `Failed to import genus changes (${response.status})`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to import genus changes: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsImportingGenusChanges(false);
    }
  }, [selectedGenusCsvFile, subreddit, user, setMessage, loadMasterSheet, loadTableData, setTableDataCache, setMasterSheetCacheKey, activeTab, selectedClass, selectedFamily, selectedGenus, selectedFlair]);

  const handleImportFading = useCallback(async () => {
    if (!selectedFadingCsvFile || !subreddit || !user) return;

    setIsImportingFading(true);
    setMessage(null);

    try {
      const { API_BASE_URL } = await import('../../../config');
      const formData = new FormData();
      formData.append('csv', selectedFadingCsvFile);

      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/import-fading`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        const processed = data.processed || 0;
        const processedImageNames = data.processedImageNames || [];
        const errors = data.errors || [];
        const skipped = data.skipped || [];

        let messageText = '';
        let messageType: 'success' | 'error' | 'warning' = 'success';
        const detailsParts: string[] = [];

        if (processed > 0) {
          messageText = `Successfully processed ${processed} fade/unfade change${processed !== 1 ? 's' : ''}`;
          // Auto-push emojis to Reddit for the updated images
          if (processedImageNames.length > 0) {
            try {
              const pushResponse = await fetch(`${API_BASE_URL}/moderator/${subreddit}/push-emojis-by-names`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${user.username}`,
                },
                body: JSON.stringify({ imageNames: processedImageNames }),
              });
              const pushText = await pushResponse.text();
              let pushData: Record<string, unknown> = {};
              try {
                pushData = pushText ? JSON.parse(pushText) : {};
              } catch {
                detailsParts.push(`Emoji push failed: non-JSON response (${pushResponse.status})`);
              }
              const shouldPoll =
                pushResponse.status === 202 ||
                pushResponse.status === 409 ||
                pushData.started === true ||
                pushData.success === true;
              if (!pushResponse.ok && pushResponse.status !== 202 && pushResponse.status !== 409) {
                detailsParts.push(
                  `Emoji push failed: ${(pushData.error as string) || (pushData.details as string) || 'Unknown error'}`
                );
              } else if (shouldPoll && user) {
                const { pollPushEmojisByNamesUntilDone } = await import('../../../utils/pushEmojisByNames');
                const finalResult = await pollPushEmojisByNamesUntilDone(
                  API_BASE_URL,
                  subreddit,
                  user.username
                );
                if (finalResult?.success === false) {
                  detailsParts.push(`Emoji push failed: ${finalResult.error || 'Unknown error'}`);
                } else if (finalResult != null) {
                  const n = finalResult.emojis_uploaded ?? 0;
                  messageText += ` and pushed ${n} emoji${n !== 1 ? 's' : ''} to Reddit`;
                  if (finalResult.errors?.length) {
                    detailsParts.push(`Emoji push class warnings: ${finalResult.errors.join('; ')}`);
                  }
                } else {
                  detailsParts.push(
                    'Emoji push did not finish in time (may still be running on the server). Check logs.'
                  );
                }
              }
            } catch (pushErr) {
              detailsParts.push(`Emoji push failed: ${pushErr instanceof Error ? pushErr.message : 'Unknown error'}`);
            }
          }
        } else {
          messageText = 'No fade/unfade changes were processed';
          messageType = 'warning';
        }

        if (errors.length > 0) {
          messageType = errors.length === processed ? 'error' : 'warning';
          detailsParts.push(`Errors (${errors.length}):`);
          errors.forEach((error: string, index: number) => {
            detailsParts.push(`${index + 1}. ${error}`);
          });
        }

        if (skipped.length > 0) {
          if (detailsParts.length > 0) detailsParts.push('');
          detailsParts.push(`Skipped (${skipped.length}):`);
          skipped.forEach((item: string, index: number) => {
            detailsParts.push(`${index + 1}. ${item}`);
          });
        }

        setMessage({
          type: messageType,
          text: messageText,
          details: detailsParts.length > 0 ? detailsParts.join('\n') : undefined
        });
        setSelectedFadingCsvFile(null);
        const fileInput = document.getElementById('fading-csv-upload-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        setTableDataCache({});
        setMasterSheetCacheKey('');
        if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
          const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
          if (selectedClass) filters.class = selectedClass;
          if (selectedFamily) filters.family = selectedFamily;
          if (selectedGenus) filters.genus = selectedGenus;
          if (selectedFlair) filters.flair = selectedFlair;
          loadTableData(activeTab, filters, true);
        }
        loadMasterSheet(true);
      } else {
        setMessage({
          type: 'error',
          text: data.error || data.details || `Failed to import fading (${response.status})`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to import fading: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsImportingFading(false);
    }
  }, [selectedFadingCsvFile, subreddit, user, setMessage, loadMasterSheet, loadTableData, setTableDataCache, setMasterSheetCacheKey, activeTab, selectedClass, selectedFamily, selectedGenus, selectedFlair]);

  const handleGenerateFadedAssetsForClass = useCallback(
    async (dryRun: boolean) => {
      if (!subreddit || !user) return;
      const cn = fadedAssetsClassInput.trim();
      if (!cn) {
        setMessage({ type: 'warning', text: 'Enter a class name (e.g. D1).' });
        return;
      }
      setIsGeneratingFadedAssets(true);
      setMessage(null);
      try {
        const { API_BASE_URL } = await import('../../../config');
        const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/faded-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.username}`,
          },
          body: JSON.stringify({ className: cn, dryRun }),
        });
        const responseText = await response.text();
        let data: {
          error?: string;
          details?: string;
          candidates?: number;
          created?: number;
          skipped?: string[];
          errors?: string[];
        };
        try {
          data = JSON.parse(responseText) as typeof data;
        } catch {
          const t = responseText.trimStart();
          const looksHtml =
            t.toLowerCase().startsWith('<!doctype') || t.startsWith('<html') || t.startsWith('<HTML');
          setMessage({
            type: 'error',
            text: looksHtml
              ? `The server returned an HTML page (HTTP ${response.status}) instead of JSON. That usually means /api did not reach this app’s Node backend (SPA fallback) or the API has not been redeployed with faded-batch. Redeploy backend + static together, or verify your host routes /api/* to Node.`
              : `Invalid JSON from server (HTTP ${response.status}).`,
            details: looksHtml ? undefined : responseText.slice(0, 800),
          });
          return;
        }
        if (!response.ok) {
          setMessage({
            type: 'error',
            text: data.error || data.details || `Request failed (${response.status})`,
          });
          return;
        }
        const createdCount = data.created ?? 0;
        const head = dryRun
          ? `Dry run: ${data.candidates ?? 0} candidate(s) for class "${cn}".`
          : `Created faded assets for ${createdCount} image(s) in class "${cn}" (inactive rows + CDN uploads).`;

        const tail: string[] = [];
        if (data.skipped?.length) {
          tail.push(
            `Skipped (${data.skipped.length}):\n${data.skipped.slice(0, 20).join('\n')}${
              data.skipped.length > 20 ? '\n…' : ''
            }`
          );
        }
        if (data.errors?.length) {
          tail.push(
            `Errors (${data.errors.length}):\n${data.errors.slice(0, 20).join('\n')}${
              data.errors.length > 20 ? '\n…' : ''
            }`
          );
        }
        const type =
          data.errors?.length && !dryRun && createdCount <= 0
            ? 'error'
            : data.errors?.length
              ? 'warning'
              : 'success';
        setMessage({
          type,
          text: head,
          details: tail.length ? tail.join('\n\n') : undefined,
        });
        if (!dryRun && (createdCount > 0 || data.skipped?.length || data.errors?.length)) {
          setTableDataCache({});
          setMasterSheetCacheKey('');
          if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
            const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
            if (selectedClass) filters.class = selectedClass;
            if (selectedFamily) filters.family = selectedFamily;
            if (selectedGenus) filters.genus = selectedGenus;
            if (selectedFlair) filters.flair = selectedFlair;
            loadTableData(activeTab, filters, true);
          }
          loadMasterSheet(true);
        }
      } catch (error) {
        setMessage({
          type: 'error',
          text: `Generate faded assets failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      } finally {
        setIsGeneratingFadedAssets(false);
      }
    },
    [
      fadedAssetsClassInput,
      subreddit,
      user,
      setMessage,
      loadMasterSheet,
      loadTableData,
      setTableDataCache,
      setMasterSheetCacheKey,
      activeTab,
      selectedClass,
      selectedFamily,
      selectedGenus,
      selectedFlair,
    ]
  );

  const handleFadeGenus = useCallback(
    async (action: 'fade' | 'unfade', dryRun: boolean) => {
      if (!subreddit || !user) return;
      const gn = fadeGenusInput.trim();
      if (!gn) {
        setMessage({ type: 'warning', text: 'Pick a genus (team), e.g. San Francisco Giants.' });
        return;
      }
      setIsFadingGenus(true);
      setMessage(null);
      if (!dryRun) setFadeGenusPreview(null);
      try {
        const { API_BASE_URL } = await import('../../../config');
        const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/fade-genus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.username}`,
          },
          body: JSON.stringify({
            genusName: gn,
            action,
            includeBandwagons: fadeGenusIncludeBandwagons,
            dryRun,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          setMessage({
            type: 'error',
            text: data.error || data.details || `Fade genus failed (${response.status})`,
          });
          return;
        }

        if (dryRun) {
          setFadeGenusPreview({
            genusName: data.genusName || gn,
            action,
            count: data.count || (data.images || []).length,
            images: data.images || [],
            bandwagons: data.bandwagons || [],
          });
          setMessage({
            type: 'success',
            text: `Preview: ${data.count || 0} image${(data.count || 0) !== 1 ? 's' : ''} in “${data.genusName || gn}” would be ${action === 'fade' ? 'faded' : 'unfaded'}.`,
            details: (data.images || [])
              .map(
                (img: { image_name: string; currently_faded?: boolean; bandwagon?: boolean }) =>
                  `${img.image_name}${img.bandwagon ? ' (bandwagon)' : ''}${img.currently_faded ? ' — already faded' : ''}`
              )
              .join('\n'),
          });
          return;
        }

        const processed = data.processed || 0;
        const processedImageNames = data.processedImageNames || [];
        const errors = data.errors || [];
        const skipped = data.skipped || [];
        const detailsParts: string[] = [];
        let messageText =
          processed > 0
            ? `${action === 'fade' ? 'Faded' : 'Unfaded'} ${processed} image${processed !== 1 ? 's' : ''} in “${data.genusName || gn}”`
            : `No ${action} changes for “${data.genusName || gn}”`;
        let messageType: 'success' | 'error' | 'warning' = processed > 0 ? 'success' : 'warning';
        if (data.assetsCreated > 0) {
          messageText += ` (created ${data.assetsCreated} faded asset${data.assetsCreated !== 1 ? 's' : ''})`;
        }

        if (processed > 0 && processedImageNames.length > 0) {
          try {
            const pushResponse = await fetch(`${API_BASE_URL}/moderator/${subreddit}/push-emojis-by-names`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.username}`,
              },
              body: JSON.stringify({ imageNames: processedImageNames }),
            });
            const pushText = await pushResponse.text();
            let pushData: Record<string, unknown> = {};
            try {
              pushData = pushText ? JSON.parse(pushText) : {};
            } catch {
              detailsParts.push(`Emoji push failed: non-JSON response (${pushResponse.status})`);
            }
            const shouldPoll =
              pushResponse.status === 202 ||
              pushResponse.status === 409 ||
              pushData.started === true ||
              pushData.success === true;
            if (!pushResponse.ok && pushResponse.status !== 202 && pushResponse.status !== 409) {
              detailsParts.push(
                `Emoji push failed: ${(pushData.error as string) || (pushData.details as string) || 'Unknown error'}`
              );
            } else if (shouldPoll && user) {
              const { pollPushEmojisByNamesUntilDone } = await import('../../../utils/pushEmojisByNames');
              const finalResult = await pollPushEmojisByNamesUntilDone(
                API_BASE_URL,
                subreddit,
                user.username
              );
              if (finalResult?.success === false) {
                detailsParts.push(`Emoji push failed: ${finalResult.error || 'Unknown error'}`);
              } else if (finalResult != null) {
                const n = finalResult.emojis_uploaded ?? 0;
                messageText += ` and pushed ${n} emoji${n !== 1 ? 's' : ''} to Reddit`;
              } else {
                detailsParts.push(
                  'Emoji push did not finish in time (may still be running on the server). Check logs.'
                );
              }
            }
          } catch (pushErr) {
            detailsParts.push(`Emoji push failed: ${pushErr instanceof Error ? pushErr.message : 'Unknown error'}`);
          }
        }

        if (data.classNames?.length) {
          detailsParts.push(
            `Old Reddit spritesheet is not updated until you regenerate/push: ${data.classNames.join(', ')} (Spritesheets tab).`
          );
        }
        if (errors.length > 0) {
          messageType = errors.length && processed === 0 ? 'error' : 'warning';
          detailsParts.push(`Errors (${errors.length}):`);
          errors.forEach((error: string, index: number) => detailsParts.push(`${index + 1}. ${error}`));
        }
        if (skipped.length > 0) {
          detailsParts.push(`Skipped (${skipped.length}):`);
          skipped.forEach((item: string, index: number) => detailsParts.push(`${index + 1}. ${item}`));
        }

        setMessage({
          type: messageType,
          text: messageText,
          details: detailsParts.length > 0 ? detailsParts.join('\n') : undefined,
        });
        setTableDataCache({});
        setMasterSheetCacheKey('');
        if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
          const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
          if (selectedClass) filters.class = selectedClass;
          if (selectedFamily) filters.family = selectedFamily;
          if (selectedGenus) filters.genus = selectedGenus;
          if (selectedFlair) filters.flair = selectedFlair;
          loadTableData(activeTab, filters, true);
        }
        loadMasterSheet(true);
      } catch (error) {
        setMessage({
          type: 'error',
          text: `Fade genus failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        });
      } finally {
        setIsFadingGenus(false);
      }
    },
    [
      fadeGenusInput,
      fadeGenusIncludeBandwagons,
      subreddit,
      user,
      setMessage,
      loadMasterSheet,
      loadTableData,
      setTableDataCache,
      setMasterSheetCacheKey,
      activeTab,
      selectedClass,
      selectedFamily,
      selectedGenus,
      selectedFlair,
    ]
  );

  const handleRebuildMasterSheetFromBaseTables = useCallback(async () => {
    if (!subreddit || !user) return;

    setIsSyncingMasterSheet(true);
    setMessage(null);

    try {
      const { API_BASE_URL } = await import('../../../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/rebuild-master-sheet-from-base-tables`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: data.message || 'Master sheet rebuilt from base tables',
        });
        setTableDataCache({});
        setMasterSheetCacheKey('');
        if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
          const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
          if (selectedClass) filters.class = selectedClass;
          if (selectedFamily) filters.family = selectedFamily;
          if (selectedGenus) filters.genus = selectedGenus;
          if (selectedFlair) filters.flair = selectedFlair;
          loadTableData(activeTab, filters, true);
        }
        loadMasterSheet(true);
      } else {
        setMessage({
          type: 'error',
          text: data.error || data.details || 'Failed to rebuild master sheet',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to rebuild: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsSyncingMasterSheet(false);
    }
  }, [subreddit, user, setMessage, loadMasterSheet, loadTableData, setTableDataCache, setMasterSheetCacheKey, activeTab, selectedClass, selectedFamily, selectedGenus, selectedFlair]);

  const handleSyncMasterSheetFromImages = useCallback(async () => {
    if (!subreddit || !user) return;

    setIsSyncingMasterSheet(true);
    setMessage(null);

    try {
      const { API_BASE_URL } = await import('../../../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/sync-master-sheet-from-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: data.message || 'Master sheet synced',
        });
        setTableDataCache({});
        setMasterSheetCacheKey('');
        if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
          const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
          if (selectedClass) filters.class = selectedClass;
          if (selectedFamily) filters.family = selectedFamily;
          if (selectedGenus) filters.genus = selectedGenus;
          if (selectedFlair) filters.flair = selectedFlair;
          loadTableData(activeTab, filters, true);
        }
        loadMasterSheet(true);
      } else {
        setMessage({
          type: 'error',
          text: data.error || data.details || `Failed to sync (${response.status})`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to sync: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsSyncingMasterSheet(false);
    }
  }, [subreddit, user, setMessage, loadMasterSheet, loadTableData, setTableDataCache, setMasterSheetCacheKey, activeTab, selectedClass, selectedFamily, selectedGenus, selectedFlair]);

  const handleAppendCsvUpload = useCallback(async () => {
    if (!selectedFile || !subreddit || !user) return;
    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setMessage({
        type: 'error',
        text: `File is too large (${(selectedFile.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 10MB.`,
      });
      return;
    }

    setIsUploading(true);
    setMessage(null);

    try {
      const { API_BASE_URL } = await import('../../../config');
      const fileText = await selectedFile.text();
      
      const response = await fetch(`${API_BASE_URL}/admin/append-csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({
          subreddit: subreddit,
          csvData: fileText,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        const importedCount = data.imported || 0;
        setMessage({
          type: 'success',
          text: `Successfully appended ${importedCount} flair entries!`,
        });
        setSelectedFile(null);
        const fileInput = document.getElementById('csv-upload-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // Reload master sheet
        if (activeTab === 'flair-sheet') {
          loadMasterSheet();
        }
      } else {
        setMessage({
          type: 'error',
          text: data.error || data.details || `Failed to append CSV (${response.status})`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to append CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, subreddit, user, setMessage, activeTab, loadMasterSheet]);

  return {
    selectedFile,
    setSelectedFile,
    selectedHistoryFiles,
    setSelectedHistoryFiles,
    isUploading,
    isImportingHistory,
    selectedGenusCsvFile,
    setSelectedGenusCsvFile,
    isImportingGenusChanges,
    selectedFadingCsvFile,
    setSelectedFadingCsvFile,
    isImportingFading,
    handleImportHistory,
    handleImportGenusChanges,
    handleImportFading,
    handleSyncMasterSheetFromImages,
    handleRebuildMasterSheetFromBaseTables,
    isSyncingMasterSheet,
    handleAppendCsvUpload,
    fadedAssetsClassInput,
    setFadedAssetsClassInput,
    isGeneratingFadedAssets,
    handleGenerateFadedAssetsForClass,
    fadeGenusInput,
    setFadeGenusInput,
    fadeGenusIncludeBandwagons,
    setFadeGenusIncludeBandwagons,
    isFadingGenus,
    fadeGenusPreview,
    handleFadeGenus,
  };
}
